import type { StateCreator } from 'zustand';

import type { AlertsSlice } from '../../interfaces/AlertsSlice';
import type { DismissAlertPayload } from '../../interfaces/AlertApiService';
import { sortActiveAlerts } from '../../utils/alertMessages';

/**
 * Singleton to hold the lazily loaded Alerts API client,
 * needed to avoid a circular dependency:
 * store → alertsSlice → alertsApiService → APIService → httpClient → store.
 */
let alertsApiSingleton: Awaited<ReturnType<typeof importAlertsApi>> | null = null;

/**
 * Dynamically import and instantiate the AlertsApiService.
 * @returns {Promise<AlertsApiService>} A promise resolving to the Alerts API service instance.
 */
async function importAlertsApi() {
  const { AlertsApiService } = await import('../../services/alertsApiService');
  return new AlertsApiService();
}

/**
 * Returns a singleton instance of AlertsApiService.
 * Performs lazy-loading on first use.
 */
async function getAlertsApi() {
  if (!alertsApiSingleton) {
    alertsApiSingleton = await importAlertsApi();
  }
  return alertsApiSingleton;
}

/**
 * Holds the polling timer ID for active alerts. Used to stop/start periodic polling.
 */
let alertsPollingTimerId: ReturnType<typeof setInterval> | null = null;

/**
 * Zustand slice middleware type placeholder.
 * Add middleware such as persist in the array if needed.
 */
type WithMW = [];

/**
 * Zustand slice for managing global alert banners and polling logic.
 */
export const createAlertsSlice: StateCreator<AlertsSlice, WithMW, [], AlertsSlice> = (
  set,
  get,
  _store,
) => ({
  /**
   * List of currently active alerts (sorted).
   */
  activeAlerts: [],
  /**
   * Whether the store is currently fetching alerts from the API.
   */
  isLoadingAlerts: false,
  /**
   * Any error that occurred during alert fetching.
   */
  alertsError: null,

  /**
   * Fetches the current list of active alerts from the backend.
   * Optionally shows a loading indicator.
   * @param {boolean} showLoadingIndicator - Whether a loading state should be reflected in the store.
   */
  fetchActiveAlerts: async (showLoadingIndicator = true) => {
    if (showLoadingIndicator) {
      set({ isLoadingAlerts: true, alertsError: null });
    }
    try {
      const api = await getAlertsApi();
      // Fetch active alerts from API, fallback to empty array
      const { alerts } = await api.getActiveAlerts();
      const list = Array.isArray(alerts) ? alerts : [];
      set({
        activeAlerts: sortActiveAlerts(list),
        alertsError: null,
      });
    } catch (err) {
      // Store the error for display or debugging
      set({ alertsError: err });
    } finally {
      if (showLoadingIndicator) {
        set({ isLoadingAlerts: false });
      }
    }
  },

  /**
   * Removes an alert from the store by its unique code.
   * @param {string} code - The unique code of the alert.
   */
  removeAlertByCode: (code: string) => {
    set((state) => ({
      activeAlerts: state.activeAlerts.filter((a) => a.code !== code),
    }));
  },

  /**
   * Stops the background polling for active alerts.
   * Clears the interval timer when it's active.
   */
  stopAlertsPolling: () => {
    if (alertsPollingTimerId !== null) {
      clearInterval(alertsPollingTimerId);
      alertsPollingTimerId = null;
    }
  },

  /**
   * Starts background polling for active alerts.
   * No-op if polling is already started.
   * The polling interval is 10 minutes.
   */
  startAlertsPolling: () => {
    if (alertsPollingTimerId !== null) {
      return;
    }
    alertsPollingTimerId = setInterval(
      () => {
        // Re-fetch alerts in background, do not trigger loading UI
        void get().fetchActiveAlerts(false);
      },
      10 * 60 * 1000, // 10 minutes
    );
  },

  /**
   * Fully resets alerts state (stops polling, clears alerts, error, loading).
   */
  resetAlerts: () => {
    get().stopAlertsPolling();
    set({
      activeAlerts: [],
      alertsError: null,
      isLoadingAlerts: false,
    });
  },

  /**
   * Sends API request to dismiss (acknowledge) an alert, then removes it from local state.
   * @param {string} code - The alert's code to dismiss.
   * @param {DismissAlertPayload} payload - The payload for the dismiss request (e.g. reason, context).
   */
  dismissAlertRequest: async (code: string, payload: DismissAlertPayload) => {
    const api = await getAlertsApi();
    await api.dismissAlert(code, payload);
    get().removeAlertByCode(code);
  },
});
